import type { GameAction, InputFrame, Vec2 } from "@golazo/shared";
import { normalize, vec } from "../core/math.js";
import { keyMap } from "./bindings.js";

/**
 * Mapa tecla -> accion abstracta. La logica de juego solo consume acciones,
 * asi que anadir gamepad mas adelante es implementar otra fuente que produzca
 * el mismo `InputFrame`, sin tocar nada mas.
 *
 * Golpear = K, y nada mas. Antes J y K hacian LO MISMO (las dos mapeaban a
 * `kick`), que es la peor clase de tecla: ocupa sitio en la mano y en el
 * cartel de controles sin anadir un solo verbo. Una accion, una tecla.
 *
 * No hay tecla de apuntar: al cargar K ya diriges con WASD, que es el mismo
 * gesto con una tecla menos.
 *
 * NO hay cambio de jugador. Llevas al TUYO de principio a fin, y el resto del
 * equipo es la IA: eso es lo que convierte desmarcarse, ofrecerse y esperar el
 * pase en decisiones tuyas. Con un boton de cambio, la respuesta a "no tengo el
 * balon" era saltar al que si lo tiene, y el juego de posicion desaparecia.
 * TAB queda libre para el marcador (mantener = tabla de estadisticas).
 *
 * O es CONTEXTUAL segun quien lleve el balon, y es el mismo verbo en los dos
 * casos — quitarse a alguien de encima:
 *   - con balon  -> regate/cano (te lo quitas de encima a el)
 *   - sin balon  -> poner el cuerpo (se lo pones tu)
 * Marcador detras + stick hacia el: O = caño atras (stick atras + carrera).
 */
export type KeyBindings = Readonly<Record<string, GameAction>>;

/*
 * Las teclas del jugador SOLO salen de `input/bindings.ts`: por defecto WASD o
 * flechas, K golpear y O regatear/cuerpo, y cada uno puede cambiarlas desde el
 * menu (boton CONTROLES). Golpear es un solo boton y la carga decide si sale
 * pase o disparo.
 */

/**
 * Dos en el mismo teclado: cada uno en su mitad, con una sola mano para
 * moverse y los dedos que le quedan para golpear y regatear. El jugador 1 a la
 * izquierda (WASD, Espacio, Shift izquierdo) y el 2 a la derecha (flechas,
 * Enter, Shift derecho). Con un mando cada uno no hace falta nada de esto.
 */
export const KEYS_PLAYER_ONE: KeyBindings = {
  KeyW: "moveUp",
  KeyS: "moveDown",
  KeyA: "moveLeft",
  KeyD: "moveRight",
  Space: "kick",
  ShiftLeft: "dash",
};

export const KEYS_PLAYER_TWO: KeyBindings = {
  ArrowUp: "moveUp",
  ArrowDown: "moveDown",
  ArrowLeft: "moveLeft",
  ArrowRight: "moveRight",
  Enter: "kick",
  NumpadEnter: "kick",
  ShiftRight: "dash",
};

/**
 * Teclas cuyo comportamiento por defecto del navegador estorba.
 *
 * TAB ya no esta aqui: no la usa ninguna accion de juego, y quien la quiere es
 * el marcador (mantener = tabla de estadisticas), que se la traga por su cuenta
 * en `match.ts`. Dejarla en esta lista era codigo muerto — este `preventDefault`
 * solo corre para teclas que SI estan mapeadas.
 */
const SWALLOWED = new Set([
  "ArrowUp",
  "ArrowDown",
  "ArrowLeft",
  "ArrowRight",
  // Espacio desplaza la pagina, y Enter y Espacio pulsarian el ultimo boton
  // con foco (el de Jugar) en mitad del partido. Con controles a gusto,
  // Espacio puede ser de cualquiera.
  "Space",
  "Enter",
  "NumpadEnter",
]);

/**
 * Alt izquierdo, asignado o no: en Firefox para Windows soltarlo abre la barra
 * de menus, que se queda las teclas siguientes, y el jugador sigue corriendo
 * con la que tenia pulsada (reportado en Discord: "corria siempre hacia
 * arriba"). AltGr no: hace falta para escribir la @ en el chat.
 */
function swallowAlt(event: KeyboardEvent): void {
  if (event.code === "AltLeft" && !isTypingTarget(event.target)) event.preventDefault();
}

/**
 * El foco esta en un sitio donde se escribe (el chat).
 *
 * Sin esto, escribir "wasd" en el chat movia al jugador, y la K le hacia
 * golpear: el teclado del juego escucha en `window` y le llegan TODAS las
 * teclas, tambien las que van a un campo de texto.
 */
export function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT";
}

export interface InputSource {
  /** Estado del input para el tick que va a simularse. */
  consume(): InputFrame;
  /** Cierra el tick y limpia los flancos, para que `pressed` dure un solo tick. */
  endTick(): void;
  dispose(): void;
}

export interface KeyboardInputOptions {
  onChange?: () => void;
  /** Que tecla hace que. Por defecto, las del jugador (`input/bindings.ts`). */
  bindings?: KeyBindings;
}

export function createKeyboardInput(
  target: Window = window,
  options: KeyboardInputOptions = {},
): InputSource {
  const { onChange } = options;
  const bindings = options.bindings ?? keyMap();
  const held = new Set<GameAction>();
  const pressed = new Set<GameAction>();
  const move: Vec2 = vec();

  const onKeyDown = (event: KeyboardEvent): void => {
    swallowAlt(event);
    if (isTypingTarget(event.target)) return;
    const action = bindings[event.code];
    if (!action) return;
    if (SWALLOWED.has(event.code)) event.preventDefault();
    if (event.repeat) return;
    held.add(action);
    pressed.add(action);
    onChange?.();
  };

  const onKeyUp = (event: KeyboardEvent): void => {
    swallowAlt(event);
    const action = bindings[event.code];
    if (!action) return;
    if (SWALLOWED.has(event.code)) event.preventDefault();
    held.delete(action);
    onChange?.();
  };

  // Al perder el foco se sueltan todas las teclas: si no, el jugador se queda
  // corriendo solo al volver a la pestana.
  const onBlur = (): void => {
    held.clear();
    pressed.clear();
    onChange?.();
  };

  /*
   * Al ponerse a escribir se sueltan todas: si ibas corriendo con la D pulsada
   * y abres el chat, el keyup de la D se lo lleva el campo de texto y el
   * jugador seguiria corriendo solo mientras escribes.
   */
  const onFocusIn = (event: FocusEvent): void => {
    if (isTypingTarget(event.target) && held.size > 0) onBlur();
  };

  target.addEventListener("keydown", onKeyDown);
  target.addEventListener("keyup", onKeyUp);
  target.addEventListener("blur", onBlur);
  target.addEventListener("focusin", onFocusIn);

  const frame: InputFrame = { move, held, pressed };

  return {
    consume(): InputFrame {
      move.x = (held.has("moveRight") ? 1 : 0) - (held.has("moveLeft") ? 1 : 0);
      move.y = (held.has("moveDown") ? 1 : 0) - (held.has("moveUp") ? 1 : 0);
      normalize(move);
      return frame;
    },
    endTick(): void {
      pressed.clear();
    },
    dispose(): void {
      target.removeEventListener("keydown", onKeyDown);
      target.removeEventListener("keyup", onKeyUp);
      target.removeEventListener("blur", onBlur);
      target.removeEventListener("focusin", onFocusIn);
      held.clear();
      pressed.clear();
    },
  };
}
